import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useWregFlow } from '@/components/webreg/useWregFlow';
import { WREG_VIEW } from '@/components/webreg/wregViews';
import { intakeEventProperties } from '@/components/webreg/screens/wregIntakeFields';
import { wregDocTitle, wregTitleKey } from '@/components/webreg/wregTitles';
import { wregStepNumber, type WregStep } from '@/components/webreg/wregSteps';
import { consumeWregNavigatedBack } from '@/components/webreg/useWregNav';
import { useOptionalWregFlowActions } from '@/components/webreg/WregFlowProvider';

type Props = {
  step: WregStep;
  children: ReactNode;
};

const WregRoute = ({ step, children }: Props) => {
  const { ready, allowed } = useWregFlow(step);
  const { trackView } = useAnalytics();
  const { t } = useTranslation();
  const seenRef = useRef<WregStep | null>(null);
  const flow = useOptionalWregFlowActions();
  const viewProperties = flow?.viewProperties;
  const startFor = flow?.startFor;

  useEffect(() => {
    if (!ready || !allowed || seenRef.current === step) return;
    seenRef.current = step;
    startFor?.(step);
    const navigatedBack = consumeWregNavigatedBack();
    const event = WREG_VIEW[step];
    if (!event) return;
    trackView({
      event,
      properties: {
        step_number: wregStepNumber(step),
        is_navigated_back: navigatedBack,
        ...intakeEventProperties(step),
        ...viewProperties?.(step),
      },
    });
  }, [allowed, ready, startFor, step, trackView, viewProperties]);

  useEffect(() => {
    if (!ready || !allowed) return;
    document.title = wregDocTitle(t(wregTitleKey(step)));
  }, [allowed, ready, step, t]);

  if (!ready || !allowed) return null;
  return <>{children}</>;
};

export { WregRoute };
